import React, { useState, useEffect } from "react";
import { Row, Col, Card, Form, Button, Alert, Spinner, Table, Badge, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageLayout from "../../layouts/PageLayout";
import FloatCardComponent from "../../components/cards/FloatCardComponent";
import { getReportesGastos } from "../../api/gastos.api.ts";

export default function GastosReportesPage() {
    const { t } = useTranslation();
    
    // Estados principales
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [datosReporte, setDatosReporte] = useState(null);
    const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
    
    // Estados de filtros
    const [filtros, setFiltros] = useState({
        fechaInicio: '',
        fechaFin: '',
        categoria: '',
        subcategoria: '',
        tipo: '',
        monto_min: '',
        monto_max: '',
        exportFormat: 'excel'
    });

    // Función para generar años disponibles
    const generarAniosDisponibles = () => {
        const anioActual = new Date().getFullYear();
        const anios = [];
        for (let i = anioActual; i >= anioActual - 5; i--) {
            anios.push(i);
        }
        return anios;
    };

    // Función para cargar datos del reporte
    const cargarDatosReporte = async (anio) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getReportesGastos(anio);
            if (response.success) {
                setDatosReporte(response.data);
            } else {
                setError('No se pudieron cargar los datos del reporte.');
            }
        } catch (err) {
            console.error('Error cargando datos de reporte:', err);
            setError('Error al cargar los datos del reporte. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    // Cargar datos al montar el componente
    useEffect(() => {
        cargarDatosReporte(anioSeleccionado);
    }, [anioSeleccionado]);

    // Función para formatear moneda
    const formatCurrency = (amount) => {
        if (!amount) return '$0';
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Función para manejar cambio de filtros
    const handleFiltroChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Función para aplicar filtros
    const aplicarFiltros = () => {
        cargarDatosReporte(anioSeleccionado);
    };

    // Función para exportar reportes
    const exportarReporte = (formato) => {
        // TODO: Implementar exportación real
        alert(`Exportando reporte en formato ${formato.toUpperCase()}...`);
    };

    // Datos con fallback a ejemplos
    const resumenPorCategoria = datosReporte?.resumen_por_categoria || [
        { categoria: 'Gastos Oficina', total: 2800000, porcentaje: 35, color: '#dc3545', items: 15 },
        { categoria: 'Edmundo Neira', total: 1200000, porcentaje: 15, color: '#0dcaf0', items: 8 },
        { categoria: 'Operativa/Terreno', total: 2500000, porcentaje: 31, color: '#198754', items: 12 },
        { categoria: 'Egresos Operativos', total: 1500000, porcentaje: 19, color: '#ffc107', items: 10 }
    ];

    const gastosRecurrentes = datosReporte?.gastos_recurrentes || [
        { concepto: 'Arriendo', monto: 450000, frecuencia: 'Mensual', ultimoPago: '2024-07-01' },
        { concepto: 'Sueldo Contador', monto: 350000, frecuencia: 'Mensual', ultimoPago: '2024-07-01' },
        { concepto: 'Plan Internet/Teléfono', monto: 45000, frecuencia: 'Mensual', ultimoPago: '2024-06-25' },
        { concepto: 'Seguro vehículo', monto: 85000, frecuencia: 'Mensual', ultimoPago: '2024-06-30' }
    ];

    const alertas = datosReporte?.alertas || [
        { tipo: 'warning', mensaje: 'Fondo Capacidad Laboral al 85% de ejecución' },
        { tipo: 'danger', mensaje: 'Presupuesto Gastos Oficina excedido en 5%' },
        { tipo: 'info', mensaje: '3 gastos pendientes de aprobación' }
    ];

    const gastosRecientes = datosReporte?.gastos_recientes || [
        { id: 1, fecha: '2024-07-15', concepto: 'Gasolina vehículo municipal', categoria: 'Operativa/Terreno', monto: 45000 },
        { id: 2, fecha: '2024-07-14', concepto: 'Material de oficina', categoria: 'Gastos Oficina', monto: 25000 },
        { id: 3, fecha: '2024-07-13', concepto: 'Honorarios profesionales', categoria: 'Edmundo Neira', monto: 180000 }
    ];

    // Generar estadísticas con datos reales o de ejemplo
    const estadisticasGenerales = datosReporte?.estadisticas_generales;
    const floats = [
        { 
            title: "Total Período", 
            digit: estadisticasGenerales?.total_periodo || 8000000, 
            icon: "attach_money", 
            variant: "lg blue" 
        },
        { 
            title: "Promedio Mensual", 
            digit: estadisticasGenerales?.promedio_mensual || 1333333, 
            icon: "trending_up", 
            variant: "lg green" 
        },
        { 
            title: "Gastos Aprobados", 
            digit: estadisticasGenerales?.gastos_aprobados || 42, 
            icon: "check_circle", 
            variant: "lg purple" 
        },
        { 
            title: "Pendientes", 
            digit: estadisticasGenerales?.gastos_pendientes || 8, 
            icon: "pending", 
            variant: "lg orange" 
        }
    ];

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Reportes de Gastos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Reportes</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Controles superiores */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Panel de Control de Reportes</h5>
                            <div className="d-flex gap-2">
                                <Form.Select 
                                    value={anioSeleccionado} 
                                    onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                                    style={{ width: 'auto' }}
                                >
                                    {generarAniosDisponibles().map(anio => (
                                        <option key={anio} value={anio}>{anio}</option>
                                    ))}
                                </Form.Select>
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => cargarDatosReporte(anioSeleccionado)}
                                    disabled={loading}
                                >
                                    {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                                    Actualizar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}
                            
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fecha Inicio</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="fechaInicio"
                                            value={filtros.fechaInicio}
                                            onChange={handleFiltroChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fecha Fin</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="fechaFin"
                                            value={filtros.fechaFin}
                                            onChange={handleFiltroChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Categoría</Form.Label>
                                        <Form.Select
                                            name="categoria"
                                            value={filtros.categoria}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todas</option>
                                            {resumenPorCategoria.map((cat, index) => (
                                                <option key={index} value={cat.categoria}>{cat.categoria}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Formato Export</Form.Label>
                                        <Form.Select
                                            name="exportFormat"
                                            value={filtros.exportFormat}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="excel">Excel</option>
                                            <option value="pdf">PDF</option>
                                            <option value="csv">CSV</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>&nbsp;</Form.Label>
                                        <div className="d-grid">
                                            <Button 
                                                variant="success" 
                                                onClick={() => exportarReporte(filtros.exportFormat)}
                                            >
                                                <i className="fas fa-download me-2"></i>
                                                Exportar
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Filtros adicionales */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Filtros de Búsqueda</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subcategoría</Form.Label>
                                        <Form.Select
                                            name="subcategoria"
                                            value={filtros.subcategoria}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todas</option>
                                            <option value="subcategoria1">Subcategoría 1</option>
                                            <option value="subcategoria2">Subcategoría 2</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Tipo de Gasto</Form.Label>
                                        <Form.Select
                                            name="tipo"
                                            value={filtros.tipo}
                                            onChange={handleFiltroChange}
                                        >
                                            <option value="">Todos</option>
                                            <option value="tipo1">Tipo 1</option>
                                            <option value="tipo2">Tipo 2</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Monto Mínimo</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="monto_min"
                                            value={filtros.monto_min}
                                            onChange={handleFiltroChange}
                                            placeholder="0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Monto Máximo</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="monto_max"
                                            value={filtros.monto_max}
                                            onChange={handleFiltroChange}
                                            placeholder="999999999"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>&nbsp;</Form.Label>
                                        <div className="d-grid">
                                            <Button variant="primary" onClick={aplicarFiltros} className="me-2">
                                                <i className="fas fa-search me-2"></i>
                                                Aplicar Filtros
                                            </Button>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Estadísticas principales */}
                <Col xl={12}>
                    <Row>
                        {floats.map((item, index) => (
                            <Col key={index} sm={6} lg={3}>
                                <FloatCardComponent 
                                    variant={item.variant}
                                    digit={formatCurrency(item.digit)}
                                    title={item.title}
                                    icon={item.icon}
                                />
                            </Col>
                        ))}
                    </Row>
                </Col>

                {/* Alertas */}
                {alertas.length > 0 && (
                    <Col xl={12}>
                        <Card className="mc-card mb-4">
                            <Card.Header>
                                <h5>Alertas y Notificaciones</h5>
                            </Card.Header>
                            <Card.Body>
                                {alertas.map((alerta, index) => (
                                    <Alert key={index} variant={alerta.tipo} className="mb-2">
                                        <i className={`fas fa-${alerta.tipo === 'warning' ? 'exclamation-triangle' : alerta.tipo === 'danger' ? 'times-circle' : 'info-circle'} me-2`}></i>
                                        {alerta.mensaje}
                                    </Alert>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                {/* Gráfico de distribución por categoría */}
                <Col xl={6}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Distribución por Categoría</h5>
                        </Card.Header>
                        <Card.Body>
                            {/* Representación visual de distribución */}
                            <div className="mb-4">
                                {resumenPorCategoria.map((item, index) => (
                                    <div key={index} className="mb-3">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-sm">{item.categoria}</span>
                                            <span className="text-sm font-weight-bold">{item.porcentaje}%</span>
                                        </div>
                                        <ProgressBar 
                                            now={item.porcentaje} 
                                            style={{ backgroundColor: '#e9ecef' }}
                                        >
                                            <ProgressBar 
                                                now={item.porcentaje} 
                                                style={{ backgroundColor: item.color }}
                                            />
                                        </ProgressBar>
                                        <div className="text-sm text-muted mt-1">
                                            {formatCurrency(item.total)} ({item.items} gastos)
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Table striped responsive className="mt-3">
                                <thead>
                                    <tr>
                                        <th>Categoría</th>
                                        <th>Cantidad</th>
                                        <th>Total</th>
                                        <th>Promedio</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resumenPorCategoria.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <span 
                                                    className="badge me-2" 
                                                    style={{ backgroundColor: item.color, width: '12px', height: '12px' }}
                                                ></span>
                                                {item.categoria}
                                            </td>
                                            <td>{item.items}</td>
                                            <td>
                                                {formatCurrency(item.total)}
                                            </td>
                                            <td>{formatCurrency(item.total / item.items)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Gastos recientes */}
                <Col xl={6}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Gastos Recientes</h5>
                        </Card.Header>
                        <Card.Body>
                            {gastosRecientes.map((gasto, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                    <div>
                                        <h6 className="mb-1">{gasto.concepto}</h6>
                                        <small className="text-muted">{gasto.categoria} • {gasto.fecha}</small>
                                    </div>
                                    <div className="text-end">
                                        <span className="text-primary fw-bold">{formatCurrency(gasto.monto)}</span>
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Gastos recurrentes */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Gastos Recurrentes</h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped responsive>
                                <thead>
                                    <tr>
                                        <th>Concepto</th>
                                        <th>Monto</th>
                                        <th>Frecuencia</th>
                                        <th>Último Pago</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gastosRecurrentes.map((gasto, index) => (
                                        <tr key={index}>
                                            <td>{gasto.concepto}</td>
                                            <td>{formatCurrency(gasto.monto)}</td>
                                            <td>{gasto.frecuencia}</td>
                                            <td>{gasto.ultimoPago}</td>
                                            <td>
                                                <Badge bg="success">Al día</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}